import { ScrollContainer } from 'app/layout/dashboardLayout/BoxLayout';
import { store } from 'app/redux/store';
import { projectsApi } from 'app/services/projects';
import {
  questionnairesApi,
  useCreateQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
} from 'app/services/questionnaires';
import { errorToast } from 'Components/Notifications';
import Spinner from 'Components/Spinner';
import { createContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import StepWizard from 'react-step-wizard';
import { useAuth } from 'utils/AuthProvider';
import MainWizardSteps from '../components/MainWizardSteps';
import { resetQuestionnaire } from '../edit/TaxQuestionnaire/actions';
import { reset as resetAgreement } from '../edit/TaxQuestionnaire/Summary/slice';
import {
  reset as resetPrepareQuestionnaire,
  selectPrepareQuestionnaire,
  SpouseOrDependent,
} from './PrepareQuestionnaire/slice';
import useCreateQuestionnaireForms from './useCreateQuestionnaireForms';
import useCreateQuestionnaireSteps from './useCreateQuestionnaireSteps';

export interface ICreateQuestionnaireProps {}

export const CreateQuestionnaireContext = createContext<{
  submitQuestionnaire: () => Promise<void>;
  isLoading: boolean;
}>({} as any);

export default function CreateQuestionnaire(props: ICreateQuestionnaireProps) {
  const cleanup = () => {
    dispatch(resetPrepareQuestionnaire());
    dispatch(resetAgreement());
    dispatch(resetQuestionnaire());
  };

  useEffect(() => {
    cleanup();
    setIsCleanedup(true);

    return () => {
      dispatch(questionnairesApi.util.invalidateTags(['Questionnaire']));
      dispatch(projectsApi.util.invalidateTags(['Project']));
      cleanup();
    }; //onUnMount
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const steps = useCreateQuestionnaireSteps();
  const forms = useCreateQuestionnaireForms();
  const { user } = useAuth();

  const [wizard, setWizard] = useState<any>();
  const [_, setDummy] = useState<number>();
  const [isCleanedup, setIsCleanedup] = useState(false);
  const getWizardInstance = (wizard: any) => {
    setWizard(wizard);
  };

  const [createQuestionnaire, { isLoading }] = useCreateQuestionnaireMutation();
  const [updateQuestionnaire] = useUpdateQuestionnaireMutation();
  const { businessId, spouseOrDependentId } = useSelector(
    selectPrepareQuestionnaire,
  );
  const prepareQuestionnaire = useSelector(selectPrepareQuestionnaire);

  const submitQuestionnaire = () => {
    return createQuestionnaire({
      project: {
        service: 'tax',
        business_ids: businessId ? [businessId] : undefined,
        project_beneficiary_attributes:
          user && Number(spouseOrDependentId)
            ? {
                beneficiary_type:
                  user.spouse?.id == Number(spouseOrDependentId)
                    ? SpouseOrDependent.Spouse
                    : SpouseOrDependent.Dependant,
                beneficiary_id: Number(spouseOrDependentId) || null,
              }
            : null,
      },
      questionnaire: {
        year: prepareQuestionnaire.year,
        fbar_only: false,
        data: {
          prepareQuestionnaire,
        },
      },
    })
      .unwrap()
      .then((payload) => {
        updateQuestionnaire({
          id: payload.project.id,
          questionnaire: {
            data: {
              prepareQuestionnaire,
              taxQuestionnaire: store.getState().taxQuestionnaire,
            },
          },
        })
          .then(() => {
            navigate(`/dashboard/questionnaires/${payload.project.id}`);
          })
          .catch((error) =>
            errorToast(
              (error as { data: { error: string; status: number } })?.data
                ?.error,
            ),
          );
      })
      .catch((error) =>
        errorToast(
          (error as { data: { error: string; status: number } })?.data?.error,
        ),
      );
  };

  return (
    <CreateQuestionnaireContext.Provider
      value={{
        submitQuestionnaire,
        isLoading,
      }}
    >
      {isCleanedup ? (
        <ScrollContainer className="p-4 justify-between">
          <StepWizard
            className="wizard z-0"
            onStepChange={() => setDummy(Math.random())}
            instance={getWizardInstance}
            transitions={{
              enterRight: '',
              enterLeft: '',
              exitRight: '',
              exitLeft: '',
            }}
            isLazyMount
            initialStep={0}
            nav={<MainWizardSteps steps={steps} />}
          >
            {forms}
          </StepWizard>
        </ScrollContainer>
      ) : (
        <div className="mt-32">
          <Spinner />
        </div>
      )}
    </CreateQuestionnaireContext.Provider>
  );
}
